import { useEffect, useRef, useState } from "react";
import { PageHeader, SectionCard } from "@/components/Primitives";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { api, authedUrl } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import TagInput from "@/components/TagInput";
import { KeyRound, Eye, EyeOff, CheckCircle2, Upload, UserCircle, Save, Lock, CalendarCheck, ImageIcon, Trash2 } from "lucide-react";
import { useBranding, refreshBranding, brandingUrl } from "@/lib/branding";
import { toast } from "sonner";

function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [busy, setBusy] = useState(false);

  const strength = (() => {
    let s = 0;
    if (next.length >= 8) s++;
    if (/[A-Z]/.test(next)) s++;
    if (/[a-z]/.test(next)) s++;
    if (/[0-9]/.test(next)) s++;
    if (/[^A-Za-z0-9]/.test(next)) s++;
    return s;
  })();
  const strengthLabel = ["Too short", "Weak", "Okay", "Good", "Strong", "Excellent"][strength];
  const strengthColor = ["bg-red-500", "bg-red-500", "bg-amber-500", "bg-yellow-500", "bg-emerald-500", "bg-emerald-500"][strength];

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (next !== confirm) { toast.error("New password and confirmation do not match"); return; }
    if (current === next) { toast.error("New password must be different from the current one"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed successfully");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to change password");
    } finally { setBusy(false); }
  };

  return (
    <SectionCard title="Change Password" subtitle="Update your account password" icon={KeyRound}>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <Label>Current password</Label>
          <div className="relative mt-1.5">
            <Input data-testid="cp-current" type={showCurrent ? "text" : "password"} value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" required />
            <button type="button" onClick={() => setShowCurrent((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div>
          <Label>New password</Label>
          <div className="relative mt-1.5">
            <Input data-testid="cp-new" type={showNext ? "text" : "password"} value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" minLength={8} required />
            <button type="button" onClick={() => setShowNext((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              {showNext ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {next.length > 0 && (
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 flex gap-1">
                {[0,1,2,3,4].map((i) => (
                  <div key={i} className={`h-1 flex-1 rounded-full ${i < strength ? strengthColor : "bg-muted"}`} />
                ))}
              </div>
              <span className="text-[10px] text-muted-foreground w-16 text-right">{strengthLabel}</span>
            </div>
          )}
        </div>
        <div>
          <Label>Confirm new password</Label>
          <Input data-testid="cp-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" minLength={8} required className="mt-1.5" />
          {confirm && next === confirm && (
            <div className="mt-1.5 text-[11px] flex items-center gap-1 text-emerald-500"><CheckCircle2 className="w-3 h-3" />Passwords match</div>
          )}
        </div>
        <Button data-testid="cp-submit" type="submit" disabled={busy}>{busy ? "Updating…" : "Change password"}</Button>
      </form>
    </SectionCard>
  );
}

/** Read-only field row */
function RO({ label, value, icon: Icon }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2.5 py-1.5 border-b border-border/30 last:border-0">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground w-32 shrink-0 pt-0.5">{label}</div>
      <div className="text-sm flex-1 min-w-0 break-words">{value}</div>
    </div>
  );
}

function ProfileCard() {
  const { user } = useAuth();
  const [emp, setEmp] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/employees/${user.id}`);
      setEmp(r.data);
      setForm({
        phone: r.data.phone || "",
        address: r.data.address || "",
        about: r.data.about || "",
        date_of_birth: r.data.date_of_birth || "",
        gender: r.data.gender || "",
        marital_status: r.data.marital_status || "",
        marriage_anniversary: r.data.marriage_anniversary || "",
        skills: r.data.skills || [],
      });
    } catch { setEmp(null); }
  };
  useEffect(() => { if (user?.id) load(); }, [user?.id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      // Drop empty string selects so we don't overwrite with junk
      ["gender", "marital_status", "marriage_anniversary", "date_of_birth"].forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });
      await api.patch(`/employees/${user.id}`, payload);
      toast.success("Profile updated");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/employees/${user.id}/avatar`, fd);
      toast.success("Photo updated");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  if (!emp) return <SectionCard title="Profile"><div className="text-sm text-muted-foreground">Loading…</div></SectionCard>;

  return (
    <>
      {/* Photo + identity — read-only identity block */}
      <SectionCard title="Identity" subtitle="Managed by HR — contact admin to update" icon={Lock}>
        <div className="flex items-center gap-4">
          <Avatar className="h-20 w-20">
            <AvatarImage src={authedUrl(emp.avatar_url)} />
            <AvatarFallback className="text-lg">{(emp.full_name||"?").split(" ").map(s=>s[0]).slice(0,2).join("")}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-2xl tracking-tight" data-testid="profile-name">{emp.full_name}</div>
            <div className="text-sm text-muted-foreground">{emp.designation} · {emp.department}</div>
            <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
              {emp.emp_code && <Badge variant="secondary" className="font-mono text-[10px]">{emp.emp_code}</Badge>}
              <Badge variant="outline" className="capitalize text-[10px]">{emp.role}</Badge>
              {emp.employment_type && <Badge variant="outline" className="capitalize text-[10px]">{emp.employment_type.replace(/_/g, " ")}</Badge>}
            </div>
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => uploadAvatar(e.target.files?.[0])} />
            <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="profile-avatar-upload">
              <Upload className="w-4 h-4 mr-1" />{uploading ? "Uploading…" : "Change photo"}
            </Button>
          </div>
        </div>
        <div className="mt-4 pt-3 border-t border-border/40">
          <RO label="Email" value={emp.email} />
          <RO label="EMP ID" value={emp.emp_code} />
          <RO label="Designation" value={emp.designation} />
          <RO label="Department" value={emp.department} />
          <RO label="Employment Type" value={emp.employment_type?.replace(/_/g, " ")} />
          <RO label="Date of Joining" value={emp.date_of_joining} />
          <RO label="Probation Ends" value={emp.probation_end_date} />
          <RO label="Notice Period" value={emp.notice_period_start ? `${emp.notice_period_start} → ${emp.notice_period_end || "?"}` : null} />
        </div>
      </SectionCard>

      {/* Editable personal details */}
      <SectionCard title="Personal details" subtitle="You can update these yourself" icon={UserCircle}>
        <form onSubmit={save} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Phone</Label><Input data-testid="pf-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
            <div><Label>Date of birth</Label><Input type="date" data-testid="pf-dob" value={form.date_of_birth || ""} onChange={(e) => set("date_of_birth", e.target.value)} /></div>
            <div>
              <Label>Gender</Label>
              <Select value={form.gender || ""} onValueChange={(v) => set("gender", v)}>
                <SelectTrigger data-testid="pf-gender"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                  <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Marital status</Label>
              <Select value={form.marital_status || ""} onValueChange={(v) => set("marital_status", v)}>
                <SelectTrigger data-testid="pf-marital"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Single</SelectItem>
                  <SelectItem value="married">Married</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.marital_status === "married" && (
              <div className="col-span-2">
                <Label>Marriage anniversary</Label>
                <Input type="date" data-testid="pf-anniv" value={form.marriage_anniversary || ""} onChange={(e) => set("marriage_anniversary", e.target.value)} />
              </div>
            )}
          </div>
          <div>
            <Label>Address</Label>
            <Textarea data-testid="pf-address" rows={2} value={form.address} onChange={(e) => set("address", e.target.value)} />
          </div>
          <div>
            <Label>About</Label>
            <Textarea data-testid="pf-about" rows={3} value={form.about} onChange={(e) => set("about", e.target.value)} placeholder="Tell your teammates about yourself…" />
          </div>
          <div>
            <Label>Skills</Label>
            <TagInput testid="pf-skills" value={form.skills} onChange={(v) => set("skills", v)} placeholder="React, Python, Kafka…" />
          </div>
          <Button type="submit" disabled={saving} data-testid="pf-save">
            <Save className="w-4 h-4 mr-1.5" />{saving ? "Saving…" : "Save changes"}
          </Button>
        </form>
      </SectionCard>
    </>
  );
}

function ClientProfileCard() {
  const { user } = useAuth();
  const [client, setClient] = useState(null);

  useEffect(() => {
    if (!user?.client_id) return;
    api.get(`/clients/${user.client_id}`).then((r) => setClient(r.data)).catch(() => setClient(null));
  }, [user?.client_id]);

  return (
    <SectionCard title="Profile" subtitle="Your client account details" icon={UserCircle}>
      <div className="flex items-center gap-4">
        <Avatar className="h-16 w-16">
          <AvatarFallback className="text-lg">
            {(user?.full_name || user?.email || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-xl tracking-tight" data-testid="profile-name">
            {user?.full_name || "Client"}
          </div>
          <div className="text-sm text-muted-foreground truncate">{user?.email}</div>
          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
            <Badge variant="outline" className="capitalize text-[10px]">{user?.role || "client"}</Badge>
            {client?.code && (
              <Badge variant="secondary" className="font-mono text-[10px]">{client.code}</Badge>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-border/40">
        <RO label="Email" value={user?.email} />
        <RO label="Organisation" value={client?.name} />
        <RO label="Client Code" value={client?.code} />
        <RO label="Industry" value={client?.industry} />
        <RO label="Website" value={client?.website} />
        <RO label="Contact" value={client?.contact_email} />
      </div>
    </SectionCard>
  );
}


function LeavePolicyCard() {
  const [policy, setPolicy] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/leave-policy").then((r) => setPolicy(r.data)).catch(() => setPolicy({
      EL: { count: 2, paid: true }, CL: { count: 1, paid: true }, Other: { count: 5, paid: false },
    }));
  }, []);

  const update = (key, patch) => {
    setPolicy((p) => ({ ...p, [key]: { ...p[key], ...patch } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/leave-policy", policy);
      toast.success("Leave policy saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  if (!policy) return <SectionCard title="Leave Policy" icon={CalendarCheck}><div className="text-sm text-muted-foreground">Loading…</div></SectionCard>;

  const rows = [
    { key: "EL", label: "Earned Leave (EL)" },
    { key: "CL", label: "Casual Leave (CL)" },
    { key: "Other", label: "Other" },
  ];

  return (
    <SectionCard title="Leave Policy" subtitle="Annual allotment and paid/unpaid status" icon={CalendarCheck}>
      <div className="space-y-3">
        {rows.map(({ key, label }) => {
          const p = policy[key] || { count: 0, paid: false };
          return (
            <div key={key} className="flex items-center gap-3 py-2 border-b border-border/40 last:border-0">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{label}</div>
                <div className="text-[11px] text-muted-foreground">Default annual count — informational only</div>
              </div>
              <div className="w-16">
                <Input
                  data-testid={`lp-count-${key}`}
                  type="number" min={0} value={p.count}
                  onChange={(e) => update(key, { count: Math.max(0, Number(e.target.value) || 0) })}
                  className="h-9 text-center"
                />
              </div>
              <div className="flex items-center gap-2 w-28 justify-end">
                <span className={`text-[11px] ${p.paid ? "text-emerald-500" : "text-muted-foreground"}`}>{p.paid ? "Paid" : "Unpaid"}</span>
                <Switch
                  data-testid={`lp-paid-${key}`}
                  checked={!!p.paid}
                  onCheckedChange={(v) => update(key, { paid: v })}
                />
              </div>
            </div>
          );
        })}
        <Button onClick={save} disabled={saving} data-testid="lp-save">
          <Save className="w-4 h-4 mr-1.5" />{saving ? "Saving…" : "Save policy"}
        </Button>
      </div>
    </SectionCard>
  );
}




/** Company Admin: replace the PRIZM logo with the company's own. */
function BrandingCard() {
  const b = useBranding();
  const [busy, setBusy] = useState(null);
  const lightRef = useRef(null);
  const darkRef = useRef(null);

  const upload = async (variant, file) => {
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) return toast.error("Use a PNG, JPG or WebP image");
    if (file.size > 2 * 1024 * 1024) return toast.error("Logo must be 2 MB or smaller");
    setBusy(variant);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/branding/logo?variant=${variant}`, fd);
      await refreshBranding();
      toast.success(variant === "light" ? "Company logo updated" : "Dark-theme logo updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(null); }
  };
  const remove = async (variant) => {
    if (!window.confirm(variant === "light" ? "Remove your logo and go back to the PRIZM logo?" : "Remove the dark-theme logo?")) return;
    setBusy(variant);
    try { await api.delete(`/branding/logo?variant=${variant}`); await refreshBranding(); toast.success("Logo removed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const Slot = ({ variant, title, hint, url, inputRef, disabled, bg }) => (
    <div className="space-y-2" data-testid={`logo-slot-${variant}`}>
      <div className="text-sm font-medium">{title}</div>
      <div className={`rounded-lg border border-border/60 h-24 flex items-center justify-center overflow-hidden ${bg}`}>
        {url ? <img src={brandingUrl(url)} alt={title} className="max-h-20 max-w-full object-contain" data-testid={`logo-preview-${variant}`} />
          : <span className="text-xs text-muted-foreground flex items-center gap-1"><ImageIcon className="w-4 h-4" />{variant === "light" ? "Using the PRIZM logo" : "Uses the main logo"}</span>}
      </div>
      <div className="text-[11px] text-muted-foreground">{hint}</div>
      <div className="flex gap-2">
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid={`logo-input-${variant}`}
          onChange={(e) => { upload(variant, e.target.files?.[0]); e.target.value = ""; }} />
        <Button size="sm" variant="outline" disabled={disabled || busy === variant} onClick={() => inputRef.current?.click()} data-testid={`logo-upload-${variant}`}>
          <Upload className="w-3.5 h-3.5 mr-1" />{busy === variant ? "Uploading…" : url ? "Replace" : "Upload"}
        </Button>
        {url && <Button size="sm" variant="ghost" className="text-red-500" disabled={busy === variant} onClick={() => remove(variant)} data-testid={`logo-remove-${variant}`}><Trash2 className="w-3.5 h-3.5 mr-1" />Remove</Button>}
      </div>
    </div>
  );

  return (
    <SectionCard title="Company branding" subtitle="Replace the PRIZM logo with your company logo" icon={ImageIcon}>
      <div className="grid sm:grid-cols-2 gap-4" data-testid="branding-card">
        <Slot variant="light" title="Logo" url={b?.logo_light_url} inputRef={lightRef} bg="bg-white"
          hint="Shown in the sidebar and on the sign-in page. PNG, JPG or WebP, up to 2 MB — a wide logo with a transparent or white background works best." />
        <Slot variant="dark" title="Dark-theme logo (optional)" url={b?.logo_dark_url} inputRef={darkRef} bg="bg-zinc-900"
          disabled={!b?.logo_light_url}
          hint={b?.logo_light_url ? "Used in dark mode. If you skip it, the main logo is used." : "Upload the main logo first."} />
      </div>
    </SectionCard>
  );
}

export default function Settings() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const isClient = user?.role === "client";
  const isAdmin = user?.role === "admin";

  return (
    <div className="space-y-6">
      <PageHeader testid="settings-header" title="Settings" subtitle="Your profile & preferences" />

      <div className="grid md:grid-cols-2 gap-6">
        {isClient ? <ClientProfileCard /> : <ProfileCard />}

        <div className="space-y-6">
          <ChangePasswordCard />

          {isAdmin && <BrandingCard />}
          {isAdmin && <LeavePolicyCard />}

          <SectionCard title="Appearance">
            <div className="flex items-center justify-between py-2">
              <div>
                <Label>Dark mode</Label>
                <div className="text-xs text-muted-foreground">Toggle between light and dark themes</div>
              </div>
              <Switch checked={theme === "dark"} onCheckedChange={toggle} data-testid="settings-theme" />
            </div>
          </SectionCard>

          <SectionCard title="Session">
            <div className="text-sm text-muted-foreground mb-3">Signed in as <span className="font-medium text-foreground">{user?.email}</span></div>
            <Button variant="destructive" onClick={logout} data-testid="settings-signout">Sign out</Button>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
